import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Bus, Plus, Trash2, Pencil, ExternalLink, X } from "lucide-react";
import { Btn, Card, Empty, Field, Input, Select, Spinner, TextArea } from "./ui";
import { UploadField } from "./UploadField";
import { MediaPreview } from "./MediaPreview";
import { errMsg, fmtDate, todayLocal } from "./api";

const EMPTY = { provincia: "", ciudad: "", fecha: todayLocal(), descripcion: "", enlaces: [], media: [], publica: false };
const TIPOS = [{ value: "foto", label: "Fotografía" }, { value: "video", label: "Video" }, { value: "entrevista", label: "Entrevista" }];

const GiraForm = ({ client, meta, initial, onSaved, onCancel }) => {
  const [f, setF] = useState(initial || EMPTY);
  const [enlace, setEnlace] = useState("");
  const [mtipo, setMtipo] = useState("foto");
  const [mtitulo, setMtitulo] = useState("");
  const [k, setK] = useState(0);
  const [busy, setBusy] = useState(false);

  const addMedia = (info) => {
    if (!info) return;
    setF((x) => ({ ...x, media: [...x.media, { file_id: info.file_id, tipo: mtipo, titulo: mtitulo || info.filename }] }));
    setMtitulo("");
    setK((n) => n + 1);
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (initial?.id) await client.put(`/gira/${initial.id}`, f);
      else await client.post("/gira", f);
      toast.success("Visita guardada");
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title={initial?.id ? "Editar visita" : "Nueva visita del ómnibus"} actions={<button onClick={onCancel}><X /></button>} testid="gira-form">
      <form onSubmit={save} className="grid md:grid-cols-3 gap-4">
        <Field label="Provincia *"><Select required value={f.provincia} placeholder="Elegí…" options={meta.provincias} onChange={(e) => setF({ ...f, provincia: e.target.value })} data-testid="gira-provincia" /></Field>
        <Field label="Ciudad *"><Input required minLength={2} value={f.ciudad} onChange={(e) => setF({ ...f, ciudad: e.target.value })} data-testid="gira-ciudad" /></Field>
        <Field label="Fecha *"><Input required type="date" value={f.fecha} onChange={(e) => setF({ ...f, fecha: e.target.value })} data-testid="gira-fecha" /></Field>
        <Field label="Actividades realizadas *" className="md:col-span-3"><TextArea required minLength={3} value={f.descripcion} onChange={(e) => setF({ ...f, descripcion: e.target.value })} data-testid="gira-descripcion" /></Field>
        <div className="md:col-span-3">
          <Field label="Enlaces a publicaciones">
            <div className="flex gap-2">
              <Input value={enlace} onChange={(e) => setEnlace(e.target.value)} placeholder="https://…" data-testid="gira-enlace-input" />
              <Btn type="button" variant="ghost" onClick={() => { const l = enlace.trim(); if (!l) return; if (!/^https?:\/\/\S+\.\S+/.test(l)) return toast.error("El enlace debe empezar con https://"); setF({ ...f, enlaces: [...f.enlaces, l] }); setEnlace(""); }} data-testid="gira-enlace-agregar">Agregar</Btn>
            </div>
          </Field>
          <ul className="mt-2 space-y-1 text-sm">
            {f.enlaces.map((l, i) => (
              <li key={i} className="flex gap-2 items-center"><span className="truncate">{l}</span><button type="button" onClick={() => setF({ ...f, enlaces: f.enlaces.filter((_, j) => j !== i) })}><X size={14} /></button></li>
            ))}
          </ul>
        </div>
        <div className="md:col-span-3 border-t pt-4">
          <div className="grid sm:grid-cols-3 gap-3 mb-3">
            <Field label="Tipo de material"><Select value={mtipo} options={TIPOS} onChange={(e) => setMtipo(e.target.value)} data-testid="gira-media-tipo" /></Field>
            <Field label="Título del archivo" className="sm:col-span-2"><Input value={mtitulo} onChange={(e) => setMtitulo(e.target.value)} placeholder="Ej: Charla en la plaza" data-testid="gira-media-titulo" /></Field>
          </div>
          <UploadField key={k} client={client} accept={mtipo === "foto" ? "imagen" : "all"} onUploaded={addMedia} testid="gira-media" label="Subir y agregar a la visita" />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
            {f.media.map((m, i) => (
              <div key={m.file_id} className="relative border text-xs">
                <MediaPreview client={client} fid={m.file_id} tipo={m.tipo === "foto" ? "imagen" : "video"} controls={false} className="w-full aspect-video" />
                <div className="p-1.5 truncate">{TIPOS.find((t) => t.value === m.tipo)?.label} · {m.titulo}</div>
                <button type="button" onClick={() => setF({ ...f, media: f.media.filter((_, j) => j !== i) })} className="absolute top-1 right-1 bg-white/90 p-0.5"><X size={14} /></button>
              </div>
            ))}
          </div>
        </div>
        <div className="md:col-span-3 flex flex-wrap items-center gap-4">
          <label className="flex items-center gap-2 text-sm cursor-pointer p-2.5 border border-emerald-300 bg-emerald-50">
            <input type="checkbox" checked={!!f.publica} onChange={(e) => setF({ ...f, publica: e.target.checked })} className="accent-emerald-700" data-testid="gira-publica" />
            Mostrar esta visita en la Gira pública
          </label>
          <Btn type="submit" busy={busy} data-testid="gira-guardar">Guardar visita</Btn>
        </div>
      </form>
    </Card>
  );
};

export const Gira = ({ client, meta }) => {
  const [items, setItems] = useState(null);
  const [prov, setProv] = useState("");
  const [orden, setOrden] = useState("fecha");
  const [edit, setEdit] = useState(null);

  const load = useCallback(() => client.get("/gira", { params: { provincia: prov } }).then((r) => setItems(r.data)).catch(() => setItems([])), [client, prov]);
  useEffect(() => { load(); }, [load]);

  const del = async (g) => {
    if (!window.confirm(`¿Eliminar la visita a ${g.ciudad}?`)) return;
    await client.delete(`/gira/${g.id}`).catch((e) => toast.error(errMsg(e)));
    load();
  };

  const sorted = [...(items || [])].sort((a, b) => (orden === "fecha" ? b.fecha.localeCompare(a.fecha) : `${a.provincia}${a.ciudad}`.localeCompare(`${b.provincia}${b.ciudad}`)));

  return (
    <div className="space-y-6" data-testid="red-gira">
      <div className="flex flex-wrap gap-3 items-center">
        <Select value={prov} placeholder="Todas las provincias" options={meta.provincias} onChange={(e) => setProv(e.target.value)} className="!w-auto" data-testid="gira-filtro-provincia" />
        <Select value={orden} options={[{ value: "fecha", label: "Orden cronológico" }, { value: "lugar", label: "Por localidad" }]} onChange={(e) => setOrden(e.target.value)} className="!w-auto" data-testid="gira-orden" />
        <Btn className="ml-auto" onClick={() => setEdit({})} data-testid="gira-nueva"><Plus size={15} /> Nueva visita</Btn>
      </div>
      {edit && <GiraForm client={client} meta={meta} initial={edit.id ? edit : null} onCancel={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
      {items === null ? <Spinner /> : sorted.length === 0 ? <Empty>Todavía no hay visitas registradas de la Gira de la Esperanza.</Empty> : (
        <ol className="relative border-l-2 border-[#F5B400] ml-3 space-y-6">
          {sorted.map((g) => (
            <li key={g.id} className="ml-6" data-testid={`gira-item-${g.id}`}>
              <span className="absolute -left-[13px] w-6 h-6 bg-[#0A192F] text-[#F5B400] flex items-center justify-center rounded-full"><Bus size={13} /></span>
              <div className="bg-white border border-gray-200 p-5">
                <div className="flex flex-wrap justify-between gap-2">
                  <div>
                    <div className="text-xs text-gray-500 font-headings uppercase tracking-wider">{fmtDate(g.fecha)}</div>
                    <h4 className="font-headings uppercase text-lg text-[#0A192F]">{g.ciudad}, {g.provincia}</h4>
                    <span className={`inline-block mt-1 px-2 py-0.5 text-[10px] font-headings uppercase tracking-wider border ${g.publica ? "bg-emerald-100 text-emerald-800 border-emerald-300" : "bg-gray-100 text-gray-600 border-gray-300"}`} data-testid={`gira-visibilidad-${g.id}`}>{g.publica ? "Pública" : "Solo panel"}</span>
                  </div>
                  <div className="flex gap-1">
                    <Btn variant="ghost" className="!px-2 !py-1" onClick={() => setEdit(g)} data-testid={`gira-editar-${g.id}`}><Pencil size={14} /></Btn>
                    <Btn variant="danger" className="!px-2 !py-1" onClick={() => del(g)} data-testid={`gira-eliminar-${g.id}`}><Trash2 size={14} /></Btn>
                  </div>
                </div>
                <p className="text-sm text-[#374151] mt-2 whitespace-pre-line">{g.descripcion}</p>
                {g.media?.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2 mt-3">
                    {g.media.map((m) => (
                      <div key={m.file_id} className="text-xs">
                        <MediaPreview client={client} fid={m.file_id} tipo={m.tipo === "foto" ? "imagen" : "video"} className="w-full aspect-video" />
                        <div className="truncate mt-0.5 text-gray-500">{m.titulo}</div>
                      </div>
                    ))}
                  </div>
                )}
                {g.enlaces?.length > 0 && (
                  <div className="flex flex-wrap gap-3 mt-3">
                    {g.enlaces.map((l) => <a key={l} href={l} target="_blank" rel="noreferrer" className="text-xs text-[#0A2463] underline inline-flex items-center gap-1"><ExternalLink size={12} />{l.replace(/^https?:\/\//, "").slice(0, 40)}</a>)}
                  </div>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
};

export default Gira;
